import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import styles from './LevelSettingsModal.module.css';

const DEFAULT_LEVELS = [
  { level: 1, label: 'Newcomer',    color: '#f6d365', icon: '1', perks: ['Join the community'],          xpRequired: 0     },
  { level: 2, label: 'Explorer',    color: '#43a0e9', icon: '2', perks: ['Unlock Direct Messaging'],     xpRequired: 500   },
  { level: 3, label: 'Contributor', color: '#43e97b', icon: '3', perks: ['Access to free resources'],    xpRequired: 1200  },
  { level: 4, label: 'Builder',     color: '#6c63ff', icon: '4', perks: ['Exclusive member badge'],      xpRequired: 2200  },
  { level: 5, label: 'Achiever',    color: '#f7971e', icon: '5', perks: ['Pin posts to community feed'], xpRequired: 3500  },
  { level: 6, label: 'Expert',      color: '#ff6b6b', icon: '6', perks: ['1:1 strategy call'],           xpRequired: 5200  },
  { level: 7, label: 'Master',      color: '#ffd700', icon: '7', perks: ['Access to VIP Courses'],       xpRequired: 7200  },
  { level: 8, label: 'Legend',      color: '#ff4da6', icon: '8', perks: ['Featured in community'],       xpRequired: 9500  },
  { level: 9, label: 'Elite',       color: '#00f5ff', icon: '9', perks: ['Free 1:1 call with founder'],  xpRequired: 12500 },
];

const PRESET_COLORS = [
  '#f6d365', '#43a0e9', '#43e97b', '#6c63ff',
  '#f7971e', '#ff6b6b', '#ffd700', '#ff4da6', '#00f5ff',
];

const PRESET_EMOJIS = [
  '1','2','3','4','5','6','7','8','9',
  '👤','🌟','🎯','🛠️','🚀','🎓','🏆','👑','💎','⚡','🔑','🔥','🦁','🦉','🐱',
];

export default function LevelSettingsModal({ levelNumber, community, onClose, onSaved }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [courses,     setCourses]     = useState([]);
  const [courseLocks, setCourseLocks] = useState({});

  /* ── parse existing levels ───────────────────────────────────────────── */
  let initialLevels = [...DEFAULT_LEVELS];
  if (community?.levelConfigs) {
    try {
      const parsed = JSON.parse(community.levelConfigs);
      if (Array.isArray(parsed) && parsed.length === 9) initialLevels = parsed;
    } catch (_) {}
  }

  const currentLevelObj = initialLevels.find(l => l.level === levelNumber) || DEFAULT_LEVELS[levelNumber - 1];

  const [label,      setLabel]      = useState(currentLevelObj.label);
  const [xpRequired, setXpRequired] = useState(currentLevelObj.xpRequired ?? 0);
  const [color,      setColor]      = useState(currentLevelObj.color || '#5d5fef');
  const [icon,       setIcon]       = useState(currentLevelObj.icon  || String(levelNumber));
  const [perks,      setPerks]      = useState(currentLevelObj.perks || []);

  /* ── fetch courses ───────────────────────────────────────────────────── */
  useEffect(() => {
    fetch(`${API}/api/courses?communityId=${community.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.courses || []);
        setCourses(list);
        const locks = {};
        list.forEach(c => { locks[c.id] = c.requiredLevel === levelNumber; });
        setCourseLocks(locks);
      })
      .catch(() => {});
  }, [community.id, levelNumber, token]);

  /* ── handlers ────────────────────────────────────────────────────────── */
  const handlePerkChange  = (i, v) => { const p = [...perks]; p[i] = v; setPerks(p); };
  const addPerk           = ()     => setPerks([...perks, '']);
  const removePerk        = (i)    => setPerks(perks.filter((_, idx) => idx !== i));
  const toggleCourseLock  = (id)   => setCourseLocks(prev => ({ ...prev, [id]: !prev[id] }));

  async function handleSave() {
    setLoading(true);
    setError(null);

    const updatedLevels = initialLevels.map(l =>
      l.level === levelNumber
        ? { ...l, label: label.trim() || `Level ${levelNumber}`, xpRequired: levelNumber === 1 ? 0 : parseInt(xpRequired, 10) || 0, color, icon, perks: perks.map(p => p.trim()).filter(Boolean) }
        : l
    );

    try {
      const res = await fetch(`${API}/api/communities/${community.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ levelConfigs: JSON.stringify(updatedLevels) }),
      });
      if (!res.ok) throw new Error('Failed to update level configuration');

      await Promise.all(courses.map(async course => {
        const isLocked  = courseLocks[course.id];
        const wasLocked = course.requiredLevel === levelNumber;
        if (isLocked !== wasLocked) {
          await fetch(`${API}/api/courses/${course.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ requiredLevel: isLocked ? levelNumber : null }),
          });
        }
      }));

      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  /* ── derived values ──────────────────────────────────────────────────── */
  const postXp   = community.postXp         ?? 30;
  const commXp   = community.commentXp      ?? 15;
  const enrollXp = community.enrollCourseXp ?? 100;
  const xpVal    = levelNumber === 1 ? 0 : parseInt(xpRequired, 10) || 0;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <h3>Edit Level {levelNumber} Settings</h3>
            <span className={styles.headerSub}>
              Customize badge appearance, requirements, perks, and course locks.
            </span>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {error && <div className={styles.errorBanner}>{error}</div>}

          {/* Live preview */}
          <div className={styles.preview}>
            <div className={styles.previewBadge} style={{ background: color }}>
              {icon}
            </div>
            <div>
              <div className={styles.previewHint}>Previewing Unlocked Badge</div>
              <div className={styles.previewName}>{label || `Level ${levelNumber}`}</div>
              <div className={styles.previewXp}>{xpVal.toLocaleString()} XP Required</div>
            </div>
          </div>

          {/* Name + XP row */}
          <div className={styles.row2}>
            <div className={styles.formGroup}>
              <label>Level Name</label>
              <input
                type="text"
                className="qeduvo-input"
                placeholder={`e.g. Level ${levelNumber}`}
                value={label}
                onChange={e => setLabel(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>XP Required to Unlock</label>
              <input
                type="number"
                className="qeduvo-input"
                disabled={levelNumber === 1}
                value={levelNumber === 1 ? 0 : xpRequired}
                onChange={e => setXpRequired(e.target.value)}
              />
            </div>
          </div>

          {levelNumber === 1 && (
            <div className={styles.disabledNote}>
              Level 1 is the starting level and always requires 0 XP.
            </div>
          )}

          {/* Effort estimator */}
          {levelNumber > 1 && xpVal > 0 && (
            <div className={styles.estimator}>
              <span className={styles.estimatorTitle}>⚖️ Difficulty & Balance Estimate:</span>
              To accumulate <strong>{xpVal.toLocaleString()} XP</strong>, a member must execute approximately:
              <ul className={styles.estimatorList}>
                <li><strong>{Math.ceil(xpVal / postXp)}</strong> posts (at +{postXp} XP each)</li>
                <li><strong>{Math.ceil(xpVal / commXp)}</strong> comments (at +{commXp} XP each)</li>
                <li><strong>{Math.ceil(xpVal / enrollXp)}</strong> course enrollments (at +{enrollXp} XP each)</li>
              </ul>
            </div>
          )}

          {/* Badge icon picker */}
          <div className={styles.formGroup}>
            <label>Badge Icon / Emoji</label>
            <div className={styles.emojiGrid}>
              {PRESET_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  className={`${styles.emojiBtn} ${icon === emoji ? styles.emojiBtnActive : ''}`}
                  onClick={() => setIcon(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Badge color picker */}
          <div className={styles.formGroup}>
            <label>Badge Theme Color</label>
            <div className={styles.colorPresets}>
              {PRESET_COLORS.map(preset => (
                <button
                  key={preset}
                  type="button"
                  className={`${styles.colorPreset} ${color.toLowerCase() === preset.toLowerCase() ? styles.colorPresetActive : ''}`}
                  style={{ background: preset }}
                  onClick={() => setColor(preset)}
                />
              ))}
            </div>
            <div className={styles.colorCustomRow}>
              <span className={styles.colorCustomLabel}>Custom Hex:</span>
              <input
                type="text"
                className="qeduvo-input"
                style={{ width: 100, padding: '4px 8px', height: 'auto', fontSize: 12 }}
                value={color}
                onChange={e => setColor(e.target.value)}
              />
              <input
                type="color"
                className={styles.colorNativeInput}
                value={color.startsWith('#') && color.length === 7 ? color : '#5d5fef'}
                onChange={e => setColor(e.target.value)}
              />
            </div>
          </div>

          {/* Course locks */}
          {courses.length > 0 && (
            <>
              <hr className={styles.divider} />
              <div className={styles.formGroup}>
                <label>
                  🔒 Unlocks Classroom Courses
                  <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 11 }}>
                    &nbsp;(Toggle courses unlocked at this level)
                  </span>
                </label>
                <div className={styles.courseList}>
                  {courses.map(course => (
                    <label key={course.id} className={styles.courseItem}>
                      <input
                        type="checkbox"
                        checked={!!courseLocks[course.id]}
                        onChange={() => toggleCourseLock(course.id)}
                      />
                      <div>
                        <div className={styles.courseTitle}>{course.title}</div>
                        {course.requiredLevel && course.requiredLevel !== levelNumber && (
                          <div className={styles.courseLockNote}>
                            Currently locked at Level {course.requiredLevel}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Perks */}
          <>
            <hr className={styles.divider} />
            <div className={styles.formGroup}>
              <label>Level Perks &amp; Benefits</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {perks.map((perk, idx) => (
                  <div key={idx} className={styles.perkRow}>
                    <input
                      type="text"
                      className="qeduvo-input"
                      placeholder="e.g. Access to VIP Courses"
                      value={perk}
                      onChange={e => handlePerkChange(idx, e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => removePerk(idx)}
                      title="Remove perk"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addPerk}
                className="qeduvo-btn outline"
                style={{ padding: '6px 12px', fontSize: 12, height: 'auto', alignSelf: 'flex-start', marginTop: 4 }}
              >
                + Add Perk
              </button>
            </div>
          </>

        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className="qeduvo-btn outline" onClick={onClose}>Cancel</button>
          <button className="qeduvo-btn primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

      </div>
    </div>
  );
}
